// Angular
import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  AfterContentChecked,
  ElementRef,
  Renderer2,
  Input,
  Injector,
  DestroyRef,
  inject,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ThemePalette } from '@angular/material/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Third-party
import { Store } from '@ngrx/store';

// Alfresco
import { SelectionState, SidebarTabRef } from '@alfresco/adf-extensions';
import {
  ContentService,
  NodesApiService,
} from '@alfresco/adf-content-services';
import {
  AppExtensionService,
  ContentApiService,
  InfoDrawerComponent,
} from '@alfresco/aca-shared';

// Project
import { getAppSelection } from '../../store/selectors/app.custom-selectors';
import { SetInfoDrawerStateAction } from '../../store/actions/app.custom-actions';
import { ContentService as WkContentService } from '../shared/services/content.service';

@Component({
  selector: 'wk-info-drawer',
  templateUrl: './wk-info-drawer.component.html',
  styleUrls: ['./wk-info-drawer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkInfoDrawerComponent
  extends InfoDrawerComponent
  implements OnInit, OnChanges, OnDestroy, AfterContentChecked
{
  @Output()
  infodrawerClosed = new EventEmitter<CustomEvent>();
  @Input()
  isEditMode: boolean;
  fileName: string;
  title = 'Properties';
  metadataComponent = 'app.components.tabs.metadata';
  isNodeWorkingCopy: boolean;
  isCompareMode = false;
  protected wkDestroyRef = inject(DestroyRef);

  set data(data: { isEditMode: boolean }) {
    this.isEditMode = data?.isEditMode;
  }

  constructor(
    injector: Injector,
    private readonly store1: Store<any>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly wkContentService: WkContentService
  ) {
    super(
      store1,
      injector.get<ContentApiService>(ContentApiService),
      injector.get<AppExtensionService>(AppExtensionService),
      injector.get<NodesApiService>(NodesApiService),
      injector.get<ContentService>(ContentService)
    );
    this.isCompareMode = this.route.parent?.snapshot.url[0].path === 'compare';
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.store1
      .select(getAppSelection)
      .pipe(takeUntilDestroyed(this.wkDestroyRef))
      .subscribe((selection) => {
        if (selection.last && this.nodeId !== selection.last.entry.id) {
          this.node = selection.last;
          this.setMetadataComponent(selection.last.entry.nodeType);
          this.fileName = selection.last.entry.name;
          this.checkNodeIsWorkingCopy(selection);
          this.setSelection(selection);
          this.setEditMode();
        }
      });
  }

  ngOnChanges(): void {
    if (this.displayNode) {
      super.ngOnChanges();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.displayNode = null;
  }

  protected setMetadataComponent(nodeType: string) {
    const { componentId, tabId } =
      this.wkContentService.getContentDetailsComponentId(nodeType) || {};
    if (componentId) {
      this.tabs = this.tabs.map((tab: SidebarTabRef) => {
        if (tab.id === tabId) {
          return { ...tab, component: componentId };
        }
        return { ...tab };
      });
    }
  }

  setSelection(selection: SelectionState) {
    if (
      !this.router.url.includes('preview') ||
      this.router.url.includes(selection.last.entry.id)
    ) {
      this.nodeId = selection.last.entry.id;
      if (selection.last?.entry?.id) {
        this.isLoading = false;
        this.setNodeToDisplay(selection.last.entry);
      }
    } else {
      this.isLoading = true;
    }
  }

  setEditMode() {
    const urlWithOutQueryParams = this.router.url.split('?')[0];
    if (urlWithOutQueryParams.includes('edit')) {
      this.isEditMode = true;
      return;
    }
    if (
      this.route.snapshot.queryParamMap.has('editMode') &&
      this.fileName.includes('(Working Copy)')
    ) {
      this.isEditMode =
        this.route.snapshot.queryParamMap.get('editMode') === 'true';
      return;
    }
    if (this.isEditMode && !this.isNodeWorkingCopy) {
      this.isEditMode = false;
    }
  }

  checkNodeIsWorkingCopy(selection: SelectionState) {
    this.isNodeWorkingCopy =
      selection.last.entry.aspectNames.includes('cm:workingcopy');
  }

  closeInfodrawer(): void {
    this.store1.dispatch(new SetInfoDrawerStateAction(false));
    this.infodrawerClosed.emit(
      new CustomEvent('closeInfodrawer', { detail: true })
    );
  }

  /** This Fix is workaround to override angular material directives in ADF component
       and in future it will be fixed.**/

  ngAfterContentChecked(): void {
    this.updateTextareaFields();
  }

  updateTextareaFields(): void {
    const textareas = this.elementRef.nativeElement.querySelectorAll(
      '.cdk-textarea-autosize'
    );
    if (textareas) {
      textareas.forEach((textarea: any) => {
        this.renderer.setStyle(textarea, 'resize', 'none');
        this.renderer.setStyle(textarea, 'max-height', 'none');
        this.renderer.setStyle(textarea, 'padding', '6px');
        this.renderer.setStyle(textarea, 'width', '306px');
        this.renderer.setStyle(textarea, 'overflow', 'hidden');
        this.renderer.setStyle(textarea, 'box-sizing', 'unset');
      });
    }
  }

  private setNodeToDisplay(node: any) {
    this.displayNode = node;
  }

  public getEntryColor(entry: any): ThemePalette {
    return entry?.color;
  }
}
